import { Button, Dialog, Input, Label, Spinner } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Section } from "./Section";

export function TwoFactorSection() {
  const t = useT();
  const qc = useQueryClient();
  const st = useQuery({
    queryKey: ["2fa"],
    queryFn: () => api<{ enabled: boolean; recoveryCodesLeft: number }>("/auth/2fa"),
  });
  const [dlg, setDlg] = useState<"setup" | "codes" | "disable" | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["2fa"] });
  if (st.isLoading || !st.data) return <Spinner />;
  return (
    <Section title={t("twofa.title")} hint={t("twofa.hint")}>
      <div className="flex items-center gap-3">
        <ShieldCheck
          className={`size-5 ${st.data.enabled ? "text-[var(--success)]" : "text-[var(--fg-faint)]"}`}
        />
        <div className="flex-1 text-sm">
          {st.data.enabled ? t("twofa.on", { n: st.data.recoveryCodesLeft }) : t("twofa.off")}
        </div>
        {st.data.enabled ? (
          <Button variant="outline" onClick={() => setDlg("disable")}>
            {t("twofa.disable")}
          </Button>
        ) : (
          <Button variant="primary" onClick={() => setDlg("setup")}>
            {t("twofa.enable")}
          </Button>
        )}
      </div>
      <Dialog
        open={dlg === "setup"}
        onOpenChange={(o) => !o && setDlg(null)}
        title={t("twofa.setupTitle")}
        description={t("twofa.setupHint")}
      >
        {dlg === "setup" && (
          <Setup
            onDone={(c) => {
              setCodes(c);
              setDlg("codes");
              void refresh();
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={dlg === "codes"}
        onOpenChange={(o) => !o && setDlg(null)}
        title={t("twofa.codesTitle")}
        description={t("twofa.codesHint")}
      >
        <Codes codes={codes} onClose={() => setDlg(null)} />
      </Dialog>
      <Dialog
        open={dlg === "disable"}
        onOpenChange={(o) => !o && setDlg(null)}
        title={t("twofa.disable")}
      >
        {dlg === "disable" && (
          <Disable
            onDone={() => {
              setDlg(null);
              void refresh();
            }}
          />
        )}
      </Dialog>
    </Section>
  );
}

function Setup({ onDone }: { onDone: (codes: string[]) => void }) {
  const t = useT();
  const [data, setData] = useState<{ secret: string; otpauth: string } | null>(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  useEffect(() => {
    api<{ secret: string; otpauth: string }>("/auth/2fa/setup", { method: "POST", body: {} }).then(
      async (d) => {
        setData(d);
        const QR = await import("qrcode");
        setQr(await QR.toDataURL(d.otpauth, { margin: 1, width: 192 }));
      },
    );
  }, []);
  const enable = useMutation({
    mutationFn: () =>
      api<{ recoveryCodes: string[] }>("/auth/2fa/enable", { method: "POST", body: { code } }),
    onSuccess: (r) => onDone(r.recoveryCodes),
  });
  if (!data) return <Spinner />;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        enable.mutate();
      }}
    >
      <div className="flex flex-col items-center gap-3">
        {qr && <img src={qr} alt="" width={192} height={192} className="rounded-xl bg-white p-2" />}
        <code className="select-all break-all text-center font-mono text-xs" dir="ltr">
          {data.secret}
        </code>
      </div>
      <div>
        <Label htmlFor="tc">{t("auth.code")}</Label>
        <Input
          id="tc"
          dir="ltr"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      {enable.error && (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {enable.error.message}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          loading={enable.isPending}
          disabled={code.length !== 6}
        >
          {t("twofa.confirm")}
        </Button>
      </div>
    </form>
  );
}

function Codes({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  return (
    <div className="space-y-4">
      <pre
        className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-xl border border-[var(--line)] bg-[var(--surface-2)] p-3 font-mono text-sm"
        dir="ltr"
      >
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </pre>
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              navigator.clipboard?.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}{" "}
            {t("action.copy")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const u = URL.createObjectURL(
                new Blob([`AI recovery codes\n\n${text}\n`], { type: "text/plain" }),
              );
              Object.assign(document.createElement("a"), {
                href: u,
                download: "qn-ai-recovery-codes.txt",
              }).click();
              setTimeout(() => URL.revokeObjectURL(u), 3000);
            }}
          >
            <Download className="size-3.5" /> {t("artifact.download")}
          </Button>
        </div>
        <Button variant="primary" onClick={onClose}>
          {t("twofa.saved")}
        </Button>
      </div>
    </div>
  );
}

function Disable({ onDone }: { onDone: () => void }) {
  const t = useT();
  const [f, setF] = useState({ password: "", code: "" });
  const m = useMutation({
    mutationFn: () => api("/auth/2fa/disable", { method: "POST", body: f }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <div>
        <Label htmlFor="dp">{t("auth.password")}</Label>
        <Input
          id="dp"
          type="password"
          dir="ltr"
          autoComplete="current-password"
          required
          value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="dc">{t("auth.code")}</Label>
        <Input
          id="dc"
          dir="ltr"
          inputMode="numeric"
          maxLength={6}
          required
          value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })}
        />
      </div>
      {m.error && (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {m.error.message}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" variant="danger" loading={m.isPending}>
          {t("twofa.disable")}
        </Button>
      </div>
    </form>
  );
}
